import React, { useMemo, type ReactElement } from 'react';
import { AuditDateRangeChip, AuditFilterChip, type AuditDateRangeValue } from './Filters';
import type { AuditFacetOptions, AuditFilters } from './auditFilterTypes';

/**
 * EAA-1821: the four facet chips the Nhật ký filter bar shows, lifted out of
 * `AuditPanelShell` — the shell had grown past the repo's split threshold and
 * this block is a self-contained concern: which facets exist, in what order,
 * and which of `credentialMode`/`targetType` the source offers.
 *
 * It renders no copy of its own; every label arrives as a prop, and the option
 * lists are the SAME memoized `AuditFacetOptions` the table header gets, so the
 * bar and the header can never disagree about a facet's vocabulary.
 */

export interface AuditFilterChipsProps {
    /** Account source offers "Chế độ"; admin source offers "Loại đối tượng". */
    isAccount: boolean;
    filters: AuditFilters;
    options: AuditFacetOptions;
    /** Grouped: the five always travel together, one per chip. */
    handlers: {
        onActionChange: (next: string[]) => void;
        onSuccessChange: (next: string[]) => void;
        onCredentialModeChange: (next: string[]) => void;
        onTargetTypeChange: (next: string[]) => void;
        onDateChange: (next: AuditDateRangeValue) => void;
    };
    /** All user-facing copy. This component holds none of its own. */
    labels: {
        action: string;
        result: string;
        mode: string;
        targetType: string;
        time: string;
        all: string;
        clearSelection: string;
    };
}

function AuditFilterChipsBase({
    isAccount,
    filters,
    options,
    handlers,
    labels,
}: AuditFilterChipsProps): ReactElement {
    /**
     * EAA-1821: "Kết quả" is one value on the wire (`success=true|false`) but
     * the chip is a checkbox list. Ticking both boxes is the same statement as
     * ticking neither — "any result" — so both collapse to the empty filter
     * rather than sending a contradictory pair the backend would have to
     * interpret.
     */
    const successValue = useMemo(
        () => (filters.success ? [filters.success] : []),
        [filters.success]
    );

    let modeChip: ReactElement;
    if (isAccount) {
        modeChip = (
            <AuditFilterChip
                label={labels.mode}
                value={filters.credentialMode}
                options={options.credentialMode}
                onChange={handlers.onCredentialModeChange}
                allLabel={labels.all}
                clearLabel={labels.clearSelection}
            />
        );
    } else {
        modeChip = (
            <AuditFilterChip
                label={labels.targetType}
                value={filters.targetType}
                options={options.targetType}
                onChange={handlers.onTargetTypeChange}
                allLabel={labels.all}
                clearLabel={labels.clearSelection}
            />
        );
    }

    return (
        <>
            <AuditFilterChip
                label={labels.action}
                value={filters.action}
                options={options.action}
                onChange={handlers.onActionChange}
                allLabel={labels.all}
                clearLabel={labels.clearSelection}
            />
            <AuditFilterChip
                label={labels.result}
                value={successValue}
                options={options.success}
                onChange={handlers.onSuccessChange}
                allLabel={labels.all}
                clearLabel={labels.clearSelection}
            />
            {modeChip}
            <AuditDateRangeChip
                label={labels.time}
                value={filters.date}
                onChange={handlers.onDateChange}
                labels={options.dateLabels}
            />
        </>
    );
}

/**
 * EAA-1821: memoized for the same reason the individual chips are — the bar
 * sits directly above a paged table and re-renders on every keystroke. Callers
 * must pass `useMemo`'d `handlers`/`labels`/`options`.
 */
export const AuditFilterChips = React.memo(AuditFilterChipsBase);
